/**
 * PreviewSheet
 *
 * General-purpose right-side sheet with a fixed header, scrollable content
 * area, and an optional fixed footer. Built on the Sheet primitive.
 *
 * Pass `header` / `footer` as ReactNode to populate the fixed regions,
 * and regular `children` for the scrollable body.
 * Width defaults to 75vw (responsive).
 */

import type { CSSProperties, ReactNode } from 'react';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { SHEET_WIDTH_ACTIVITY } from '../constants';

interface PreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** CSS width value. Defaults to '75vw' for responsive 75% viewport width. */
  width?: string;
  /** When true, renders a pulsing skeleton instead of header/children/footer. */
  loading?: boolean;
  /** Fixed header content. Rendered inside a bordered, non-scrollable bar. */
  header?: ReactNode;
  /** Extra classes on the header wrapper. */
  headerClassName?: string;
  /** Fixed footer content. Rendered inside a bordered, non-scrollable bar. */
  footer?: ReactNode;
  /** Extra classes on the footer wrapper. */
  footerClassName?: string;
  /** Scrollable main content. */
  children: ReactNode;
  /** Extra classes on the scrollable content area. */
  contentClassName?: string;
}

const SHIMMER = 'bg-dinnerplate-white animate-shimmer';

function Bone({ className }: { className?: string }) {
  return <div className={cn(SHIMMER, className)} />;
}

function PreviewSheetSkeleton() {
  return (
    <div className="flex flex-col h-full">
      {/* Header — matches OrderDetailHeader: icon + ref number | status dropdown */}
      <div className="flex-shrink-0 border-b border-dinnerplate-white px-6 py-3 flex items-center justify-between md:py-0 md:h-[65px]">
        <div className="flex items-center gap-3">
          <Bone className="h-5 w-5 rounded" />
          <Bone className="h-5 rounded w-24" />
        </div>
        <Bone className="h-10 rounded-input w-[184px]" />
      </div>

      {/* Body — matches OrderDetails layout */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 md:px-8">
        {/* Order section wrapper — matches rounded-input border p-6 space-y-8 */}
        <div className="space-y-8 rounded-input border border-dinnerplate-white bg-off-white p-6">

          {/* CustomerBillToCard skeleton — space-y-4, grid-cols-2 header + content */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-center md:gap-8">
              <Bone className="h-5 rounded w-20" />
              <div className="flex items-center justify-between">
                <Bone className="h-5 rounded w-14" />
                <Bone className="h-8 w-8 rounded-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
              <div className="space-y-1">
                <Bone className="h-3.5 rounded w-44" />
                <Bone className="h-3 rounded w-56" />
              </div>
              <div className="space-y-1">
                <Bone className="h-3.5 rounded w-36" />
                <Bone className="h-3 rounded w-48" />
              </div>
            </div>
          </div>

          {/* SalespersonSourceCard skeleton — grid-cols-2 gap-8, avatar + name | source + paid badge */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            <div className="space-y-4">
              <Bone className="h-5 rounded w-24" />
              <div className="flex items-center gap-4">
                <Bone className="h-12 w-12 rounded-full" />
                <Bone className="h-3.5 rounded w-28" />
              </div>
            </div>
            <div className="space-y-4">
              <Bone className="h-5 rounded w-16" />
              <div className="space-y-1">
                <Bone className="h-3.5 rounded w-32" />
                <Bone className="h-3 rounded w-48" />
              </div>
            </div>
          </div>

          {/* OrderYieldCard skeleton — grid-cols-2 gap-8, title + badge, metrics */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Bone className="h-5 rounded w-32" />
                <Bone className="h-6 rounded-md w-14" />
              </div>
              <div className="flex flex-col gap-1.5 md:flex-row md:gap-8">
                <Bone className="h-3.5 rounded w-28" />
                <Bone className="h-3.5 rounded w-28" />
                <Bone className="h-3.5 rounded w-32" />
              </div>
            </div>
          </div>
        </div>

        {/* EventsSectionCard skeleton — title + count badge | empty state */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Bone className="h-6 rounded w-16" />
              <Bone className="h-8 w-8 rounded-md" />
            </div>
            <Bone className="h-12 rounded-xs w-32" />
          </div>
          <Bone className="rounded-input border border-dinnerplate-white py-36" />
        </div>
      </div>

    </div>
  );
}

export function PreviewSheet({
  open,
  onOpenChange,
  width = '75vw',
  loading = false,
  header,
  headerClassName,
  footer,
  footerClassName,
  children,
  contentClassName,
}: PreviewSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className={cn(
          'w-full max-w-none border-l-0 bg-off-white p-0 overflow-hidden flex flex-col [&>button]:hidden md:w-[var(--preview-sheet-w)] md:max-w-[var(--preview-sheet-max)] md:rounded-l-input md:border-l md:border-dinnerplate-white',
        )}
        style={{
          '--preview-sheet-w': width,
          '--preview-sheet-max': `${SHEET_WIDTH_ACTIVITY}px`,
        } as CSSProperties}
      >
        {loading ? (
          <PreviewSheetSkeleton />
        ) : (
          <>
            {header != null ? (
              <div
                className={cn(
                  'flex-shrink-0 border-b border-dinnerplate-white px-6 py-3 flex items-center justify-between md:py-0 md:h-[65px]',
                  headerClassName
                )}
              >
                {header}
              </div>
            ) : null}

            <div className={cn('flex-1 overflow-y-auto px-6 py-6 md:px-8', contentClassName)}>
              {children}
            </div>

            {footer != null ? (
              <div
                className={cn(
                  'flex-shrink-0 border-t border-dinnerplate-white px-6 py-3 flex items-center justify-between md:py-0 md:px-8 md:h-23',
                  footerClassName
                )}
              >
                {footer}
              </div>
            ) : null}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
